import { useLogto } from '@logto/react';
import { useCallback } from 'react';
import { API_BASE_URL, API_RESOURCE } from '@/auth/LogtoConfig';
import { useAuth } from '@/auth/useAuth';

export class ApiError extends Error {
  status: number;
  code: string;
  metadata?: unknown;

  constructor(status: number, code: string, message: string, metadata?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.metadata = metadata;
  }
}

export function useApiClient() {
  const { getAccessToken } = useLogto();
  const { login } = useAuth();

  const fetchWithAuth = useCallback(
    async <T = unknown>(path: string, init?: RequestInit): Promise<T> => {
      let token: string | undefined;
      try {
        token = await getAccessToken(API_RESOURCE);
      } catch {
        login();
        throw new Error('Session expired, redirecting to login');
      }

      if (!token) {
        login();
        throw new Error('No access token available');
      }

      const headers = new Headers(init?.headers ?? undefined);
      headers.set('Authorization', `Bearer ${token}`);
      if (!headers.has('Content-Type') && init?.body && !(init.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
      }

      const response = await fetch(`${API_BASE_URL}${path}`, {
        ...init,
        headers,
      });

      if (!response.ok) {
        const errorBody = await response
          .json()
          .catch(() => ({ error: 'unknown_error', message: `HTTP ${response.status}` }));
        const field =
          errorBody &&
          typeof errorBody === 'object' &&
          errorBody.metadata &&
          typeof errorBody.metadata === 'object' &&
          'field' in errorBody.metadata
            ? String((errorBody.metadata as { field?: unknown }).field ?? '')
            : '';
        const baseMessage = errorBody.message || errorBody.error || `HTTP ${response.status}`;
        const message = field ? `${baseMessage} (field: ${field})` : baseMessage;
        throw new ApiError(
          response.status,
          errorBody.error || 'unknown_error',
          message,
          errorBody.metadata
        );
      }

      if (response.status === 204) {
        return undefined as T;
      }

      const contentType = response.headers.get('Content-Type') || '';
      if (contentType.includes('application/json')) {
        return response.json();
      }
      return (await response.text()) as T;
    },
    [getAccessToken]
  );

  // Unauthenticated fetch for public endpoints (e.g. invitation preview).
  const fetchPublic = useCallback(async <T = unknown>(path: string, init?: RequestInit): Promise<T> => {
    const headers = new Headers(init?.headers ?? undefined);
    if (!headers.has('Content-Type') && init?.body && !(init.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }
    const response = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });
    if (!response.ok) {
      const errorBody = await response
        .json()
        .catch(() => ({ error: 'unknown_error', message: `HTTP ${response.status}` }));
      const field =
        errorBody &&
        typeof errorBody === 'object' &&
        errorBody.metadata &&
        typeof errorBody.metadata === 'object' &&
        'field' in errorBody.metadata
          ? String((errorBody.metadata as { field?: unknown }).field ?? '')
          : '';
      const baseMessage = errorBody.message || errorBody.error || `HTTP ${response.status}`;
      const message = field ? `${baseMessage} (field: ${field})` : baseMessage;
      throw new ApiError(
        response.status,
        errorBody.error || 'unknown_error',
        message,
        errorBody.metadata,
      );
    }
    if (response.status === 204) return undefined as T;
    return response.json();
  }, []);

  return { fetchWithAuth, fetchPublic };
}
